import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import api, { money, fmtDate } from "@/lib/api";
import { DOC_TYPES } from "@/lib/constants";
import { useApp } from "@/context/AppContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function DocumentsList({ type }) {
  const cfg = DOC_TYPES[type];
  const { settings } = useApp();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const sym = settings?.currency_symbol || "$";

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    const t = setTimeout(() => {
      api.get(cfg.endpoint, { params }).then((r) => setDocs(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [q, status, cfg.endpoint]);

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={`${docs.length} documentos`}>
        <Button asChild className="bg-amber-700 hover:bg-amber-800" data-testid={`new-${type}-button`}>
          <Link to={`${cfg.base}/nueva`}><Plus className="h-4 w-4 mr-1" />Nueva {cfg.single.toLowerCase()}</Link>
        </Button>
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
          <Input className="pl-9 bg-white" placeholder="Buscar por cliente o folio" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${type}-search-input`} />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-48 bg-white" data-testid={`${type}-status-filter`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {cfg.statuses.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border bg-white overflow-x-auto" data-testid={`${type}-list`}>
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-4 py-3">Folio</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Estado</th><th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {docs.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-stone-400">No hay documentos todavía</td></tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} onClick={() => nav(`${cfg.base}/${d.id}`)} className="cursor-pointer hover:bg-amber-50/50 transition-colors" data-testid={`${type}-row-${d.folio}`}>
                <td className="px-4 py-3 font-mono font-semibold text-amber-800">{d.folio}</td>
                <td className="px-4 py-3 font-medium">{d.client_name}</td>
                <td className="px-4 py-3 text-stone-600">{fmtDate(d.date)}</td>
                <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                <td className="px-4 py-3 text-right font-mono font-semibold">{money(d.total, sym)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
